import { action } from '@ember/object';
import { debounce, once } from '@ember/runloop';
import Component from '@glimmer/component';

import themed from 'webapp/decorators/themed';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';

interface Args {
	text: string;
	mode: string;
	isReadOnly: boolean; // cannot change text, but can select etc
	onTextChange: (text: string) => void;
	onValidChange?: (valid: boolean) => void;
	selectedRowIndex?: number;
	onRowChange?: (rowIndex: number) => void;
	onScroll?: (scrollTop: number) => void;
	theme?: string;
	height?: number | null; // will trigger editor resize on change, actual value is not used
	extraClass?: string;
}

@themed()
export default class FormCodeEditorComponent extends Component<Args> {
	declare theme: string; // from @themed
	declare themeClass: string; // from @themed

	editor: AceEditor | null = null;
	node: HTMLElement | null = null;

	get themeId(): string {
		const isDefaultTheme = ['light', 'dark'].includes(this.theme);
		if (isDefaultTheme) {
			return `code_${this.theme}`;
		} else {
			return this.theme;
		}
	}

	@action
	setupEditor(node: HTMLElement): void {
		this.node = node;

		if (this.editor) {
			return;
		}

		const editor = window.ace.edit(node);

		editor.$blockScrolling = Infinity;
		editor.setTheme(`ace/theme/${this.themeId}`);

		const session = editor.getSession();
		session.setMode(`ace/mode/${this.args.mode}`);
		session.on('change', (_changeData: unknown, session: AceSession) => {
			const newText = session.getValue();
			this.args.onTextChange(newText);
		});

		session.on('changeAnnotation', () => {
			// checking args.text to not mark empty as invalid
			if (this.args.text && this.args.onValidChange) {
				const errors = session.getAnnotations().filter((item) => item.type === 'error');
				this.args.onValidChange(!errors.length);
			}
		});

		const onScroll = this.args.onScroll;
		if (onScroll) {
			session.on('changeScrollTop', (scrollPos: number) => {
				onScroll(scrollPos);
			});
		}

		session.setUseWrapMode(false); // don't wrap bcos of possible radiobuttons on the side
		session.setWrapLimitRange(null, null);

		editor.setReadOnly(this.args.isReadOnly);

		editor.focus();

		session.selection.on('changeCursor', () => {
			debounce(this, this.onRowChange, 100);
		});

		this.editor = editor;

		if (this.args.selectedRowIndex) {
			editor.moveCursorTo(this.args.selectedRowIndex, 0);
		}
	}

	@action
	updateText(): void {
		const editor = this.editor;
		if (editor && editor.getSession().getValue() !== this.args.text) {
			editor.getSession().setValue(this.args.text, -1);
			this.onRowChange();
		}
	}

	@action
	updateReadOnly(): void {
		this.editor?.setReadOnly(this.args.isReadOnly);
	}

	@action
	updateSelectedRowIndex(): void {
		const selectedRowIndex = this.args.selectedRowIndex;
		if (typeof selectedRowIndex === 'number') {
			const editor = this.editor;

			if (!editor) {
				return;
			}

			const currentRange = editor.selection.getRange(),
				currentPosition = editor.getCursorPosition();
			if (
				currentPosition.row !== selectedRowIndex ||
				currentRange.start.row !== currentRange.end.row
			) {
				editor.selection.setRange({
					start: { row: selectedRowIndex, column: 0 },
					end: { row: selectedRowIndex, column: 0 },
				});
			}
		}
	}

	@action
	onHeightUpdate(): void {
		this.editor?.resize();
	}

	@action
	onThemeChange(): void {
		this.editor?.setTheme(`ace/theme/${this.themeId}`);
	}

	private onRowChange(): void {
		const onRowChange = this.args.onRowChange,
			editor = this.editor; // always set, called after setupEditor
		if (onRowChange && editor) {
			try {
				const row = editor.getCursorPosition().row;
				if (row !== this.args.selectedRowIndex) {
					onRowChange(row);
					// check after if the outside given index has changed
					// if not (means was not valid), we need to set the one in editor back
					once(this, this.updateSelectedRowIndex);
				}
			} catch (_e) {
				// skip
			}
		}
	}
	<template>
		{{! oneliner, bcos of unwelcome extra whitespace in editor otherwise }}
		{{! we added ace_editor class because it's removed during theme switch }}
		<div
			class="code-editor ace_editor {{this.themeClass}} {{@extraClass}}"
			{{didInsert this.setupEditor}}
			{{didUpdate this.updateText @text}}
			{{didUpdate this.updateReadOnly @isReadOnly}}
			{{didUpdate this.updateSelectedRowIndex @selectedRowIndex}}
			{{didUpdate this.onHeightUpdate @height}}
			{{didUpdate this.onThemeChange @theme}}
		>
			{{@text}}
		</div>
	</template>
}
